/** @jsxImportSource @emotion/react */

import styled from "@emotion/styled";

import { useState } from "react";

import DeleteIcon from "@material-ui/icons/Delete";
import DublicateIcon from "@material-ui/icons/FileCopy";
import ShowIcon from "@material-ui/icons/Visibility";
import HideIcon from "@material-ui/icons/VisibilityOff";
import LockIcon from "@material-ui/icons/Lock";
import UnlockIcon from "@material-ui/icons/LockOpen";
import HeightIcon from "@material-ui/icons/Height";
import UnfoldIcon from "@material-ui/icons/ChevronRight";
import FoldIcon from "@material-ui/icons/KeyboardArrowDown";
import CollageIcon from "@material-ui/icons/ViewCompact";

import { SortableElement } from "react-sortable-hoc";
import confirm from "components/Dialog";
import t from "translations";

const LayerWrap = styled.li`
  z-index: 999;
  list-style: none;
  border-bottom: 1px solid #595959;
  padding: 5px 10px;
  display: flex;
  justify-content: space-between;
  cursor: row-resize;
  &.active {
    background: #555;
  }
  :hover {
    background: #444444;
  }
`;

const ControlsWrap = styled.div`
  .draghandle {
    cursor: row-resize;
  }
  margin-left: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  > svg {
    color: #b1aeae;
    cursor: pointer;
    width: 1.4em;
    height: 1.4em;
  }
`;

const CollageLayerWrap = styled.div`
  border-bottom: 1px solid #595959;
  padding: 5px 10px;
  padding-left: 0;
  margin-left: -8px;
  cursor: pointer;
  > div {
    display: flex;
    flex-direction: row;
    > svg {
      width: 1.5em;
      height: 1.5em;
    }
  }
`;

const CollageImages = styled.div`
  margin-top: 5px;
  display: flex;
  color: white;
  flex-direction: column !important;
  > div {
    padding: 5px 10px;
    margin-left: 38px;
    border-bottom: 1px solid #484848;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    &.active {
      background: #555;
    }
  }
`;

const LayerPreview = styled.div`
  width: 60px;
  height: 60px;
  border: 1px solid #888;
  border-radius: 8px;
  background: none;
  overflow: hidden;
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
  cursor: default;
  > img {
    max-width: 100%;
    max-height: 100%;
    margin: auto;
    pointer-events: none;
  }
  > span,
  div {
    position: absolute;
    bottom: 0px;
    right: 0px;
    color: white;
    font-size: 9px;
    background: #5d5d5db8;
    font-weight: bold;
    padding: 1px;
    text-transform: capitilize;
  }
  > div {
    top: 0px;
    bottom: auto;
    display: block;
  }
`;

const CollageTitle = styled.h4`
  color: white;
  margin: 6px 0;
  user-select: none;
  margin-left: 10px;
  font-size: 17px;
`;

const CollageControls = styled(ControlsWrap)`
  position: absolute;
  right: 10px;
`;

export const CollageLayer = ({ obj }) => {
  const [isFolded, setIsFolded] = useState(false);
  const { images, canvas } = obj;
  return (
    <CollageLayerWrap>
      <div
        onClick={() => {
          setIsFolded(!isFolded);
        }}
      >
        {isFolded ? <UnfoldIcon /> : <FoldIcon />}
        <CollageIcon />
        <CollageTitle>{t.collageSection}</CollageTitle>
        <CollageControls>
          <DeleteIcon
            onClick={async e => {
              e.stopPropagation();
              const result = await confirm.show({
                text: t.removeCollageSectionWarn,
                title: t.removeCollageSectionWarnTitle
              });
              if (result) {
                canvas.renderOnAddRemove = false;
                canvas.stateful = false;
                canvas
                  .getObjects()
                  .filter(o => o.mask || o.clipId)
                  .forEach(o => canvas.remove(o));
              }
              canvas.renderOnAddRemove = true;
              canvas.stateful = true;
              canvas.requestRenderAll();
            }}
          />
        </CollageControls>
      </div>
      {!isFolded && (
        <CollageImages>
          {!images.length && <div>{t.noCollageImages}</div>}
          {images.map(image => {
            console.log(image);
            return (
              <div
                onClick={() => {
                  if (image.canvas) {
                    image.canvas.setActiveObject(image);
                    image.selectable = true;
                    image.evented = true;
                  }
                }}
                className={
                  image.canvas.getActiveObject() === image
                    ? "active"
                    : "not-active"
                }
              >
                <LayerPreview>
                  <img src={image.src} alt="img" />
                  <span>Collage img</span>
                </LayerPreview>
                <ControlsWrap>
                  <DeleteIcon
                    onClick={e => {
                      image.canvas.remove(image);
                    }}
                  />
                </ControlsWrap>
              </div>
            );
          })}
        </CollageImages>
      )}
    </CollageLayerWrap>
  );
};

export const Layer = SortableElement(({ obj }) => {
  const previewObjects = ["image", "svg", "i-text"];
  let previewImage = "";
  if (previewObjects.includes(obj.type)) {
    if (obj.type === "i-text") {
      let previewBase64 = 0;
      const objCopy = window.fabric.util.object.clone(obj);
      objCopy.visible = true;
      objCopy.objectCaching = false;
      objCopy.globalCompositeOperation = "source-over";
      try {
        previewBase64 = objCopy.toDataURL({
          format: "image/png",
          multiplier: 55 / objCopy.getScaledWidth()
        });
        previewImage = <img src={previewBase64} alt="text" />;
      } catch {
        previewImage = "";
      }
    }
    if (obj.type === "image") {
      if (obj.thumb) {
        previewImage = <img src={obj.thumb} alt="img" />;
      }
    }
  }

  const changeVisibility = () => {
    obj.visible = !obj.visible;
    obj.canvas.fire("object:modified", { target: obj });
    obj.canvas.requestRenderAll();
  };

  const changeLock = () => {
    obj.selectable = !obj.selectable;
    obj.evented = !obj.evented;
    obj.canvas.fire("object:modified", { target: obj });
    obj.canvas.requestRenderAll();
  };

  const layerType = obj.clipId ? "collage" : obj.type;

  return (
    <LayerWrap
      onClick={() => {
        if (obj.canvas) {
          obj.canvas.setActiveObject(obj);
        }
      }}
      className={obj.canvas.getActiveObject() === obj ? "active" : "not-active"}
    >
      <div>
        <LayerPreview className="preview">
          {previewImage}
          <span>{layerType}</span>
        </LayerPreview>
      </div>
      <ControlsWrap>
        <HeightIcon className="draghandle" />
        {!obj.evented ? (
          <LockIcon onClick={changeLock} />
        ) : (
          <UnlockIcon onClick={changeLock} />
        )}
        {obj.visible ? (
          <ShowIcon onClick={changeVisibility} />
        ) : (
          <HideIcon onClick={changeVisibility} />
        )}
        <DeleteIcon
          onClick={e => {
            obj.canvas.remove(obj);
          }}
        />
        <DublicateIcon
          onClick={() => {
            const canvas = obj.canvas;
            obj.clone(cloned => {
              cloned.top += cloned.height * 0.2 * cloned.scaleY;
              cloned.left += cloned.width * 0.2 * cloned.scaleX;
              cloned.size = { ...obj.size };
              cloned.hash = obj.hash;
              cloned.thumb = obj.thumb;
              cloned.generateId();
              canvas.add(cloned);
              canvas.discardActiveObject();
              canvas.setActiveObject(cloned);
            });
          }}
        />
      </ControlsWrap>
    </LayerWrap>
  );
});
